/**
 * Customers Store (Pinia)
 *
 * @version 1.0.0
 * @description Manages Metodo ERP customer data, DDM locations, and highlighted counts
 * @migrated-from store/customers/customers.ts (Vuex 4)
 * @note WebSocket target — websocket store dispatches updates to this store
 */

import { defineStore, storeToRefs } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { standardizeErrorMessage, toApiError } from '@/utils/utils'
import { useNavigationStore } from '@/stores/useNavigationStore'
import type { Pagination, ApiRequestOptions, SelectOption } from '@/stores/types'

// =============================================================================
// Types (re-exported from @/models for backward compatibility)
// =============================================================================

import type { MetodoCliente, MetodoClienteDDM } from '@/models'

/** Alias kept for backward compatibility. */
export type MetodoCustomer = MetodoCliente

/** Alias kept for backward compatibility. */
export type CustomerDDM = MetodoClienteDDM

export interface CustomerDDMSelectOption {
  display: string
  value: number | null
  customer: string | null
}

// =============================================================================
// Store
// =============================================================================

export const useCustomersStore = defineStore('customers', () => {
  const { companyApiUrl } = storeToRefs(useNavigationStore())

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const loading = ref(false)
  const customersMix = ref<any[]>([])
  const customers = ref<MetodoCustomer[]>([])
  const customersDDM = ref<CustomerDDM[]>([])
  const customersHighlighted = ref(0)
  const customersPagination = ref<Pagination | null>(null)
  const currentOptions = ref<ApiRequestOptions>({})

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  /** Formatted customer list for v-select / v-autocomplete. */
  const customersSelectSRV = computed<SelectOption<string | null>[]>(() => {
    const options = customers.value
      .filter((c) => c.CODCLISRV !== null && c.CODCLISRV.trim() != "") // Ensure CODCLISRV exists
      .map((c) => ({
        display: `${c.DSCCONTO1 ?? ''} ${c.DSCCONTO2 ?? ''}`.trim(),
        value: c.CODCLISRV ?? '',
      }))
      .sort((a, b) => a.display.localeCompare(b.display))

    // console.log('customersSelectSRV options:', options) // Debug log
    return [{ display: '', value: null }, ...options]
  })

  /** Formatted DDM list for v-select / v-autocomplete. */
  const customersDDMSelectSRV = computed<CustomerDDMSelectOption[]>(() => {
    const options = customersDDM.value
      //.filter((ddm) => ddm.customer?.CODCLISRV !== null && ddm.customer!.CODCLISRV.trim() != "") // Ensure CODICE exists
      .map((ddm) => {
        if (!ddm.customer) {
          return { display: 'Nessuna DDM', value: null, customer: null }
        }
        return {
          display: `${ddm.customer.CODCLISRV} - ${ddm.LOCALITA ?? ''} ${ddm.INDIRIZZO ?? ''}`.trim(),
          value: ddm.CODICE,
          customer: ddm.customer.CODCLISRV ?? null,
        }
      })
      .sort((a, b) => a.display.localeCompare(b.display))

    return [{ display: '', value: null, customer: null }, ...options]
  })

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Fetch a single customer by code.
   */
  async function getCustomer(customerCode: string): Promise<MetodoCustomer> {
    loading.value = true
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/metodo/customer/${customerCode}`)
      return data
    } catch (error) {
      // ApiError (non stringa): la scheda cliente distingue 403 fuori perimetro da 404
      throw toApiError(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch mixed customers list (prospects + customers).
   */
  async function fetchCustomersMix(): Promise<any> {
    loading.value = true
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/customers-mix`)
      customersMix.value = data
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch count of highlighted/flagged customers.
   */
  async function fetchHighlightedCount(): Promise<number> {
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/metodo/customer/highlighted`)
      customersHighlighted.value = data
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  /**
   * Fetch customers list with pagination/filter options.
   */
  async function fetchCustomers(options?: ApiRequestOptions): Promise<any> {
    loading.value = true
    if (options != null) currentOptions.value = options

    try {
      const { data } = await axios.get(`${companyApiUrl.value}/metodo/customer`, {
        params: options ?? currentOptions.value,
      })
      customers.value = data.data ?? data
      customersPagination.value = data.pagination ?? null
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch DDM (Destinazioni Diverse Merce) list.
   * Also extracts unique customers from DDM data.
   */
  async function fetchCustomersDDM(options?: ApiRequestOptions): Promise<any> {
    loading.value = true
    if (options != null) currentOptions.value = options

    try {
      const { data } = await axios.get(`${companyApiUrl.value}/metodo/customer/ddm`, {
        params: options ?? currentOptions.value,
      })
      // Normalize CODICE to number — API may return it as string
      const rawList: CustomerDDM[] = data.data ?? data
      customersDDM.value = rawList.map((ddm) => ({
        ...ddm,
        CODICE: ddm.CODICE != null ? Number(ddm.CODICE) : ddm.CODICE,
      }))
      // Extract unique customers from DDM
      const uniqueCustomers: MetodoCustomer[] = []
      for (const ddm of customersDDM.value) {
        if (ddm.customer) uniqueCustomers.push(ddm.customer)
      }
      customers.value = [...new Map(uniqueCustomers.map((c) => [c.CODCLIMKT ?? c.CODCLISRV, c])).values()]
      // Return normalized data (not raw) so consumers get correct types
      return { ...data, data: customersDDM.value }
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Update a customer (patch).
   */
  async function patchCustomer(customer: Partial<MetodoCustomer> & { CODCLIMKT: string }): Promise<any> {
    loading.value = true
    try {
      const { data } = await axios.put(`${companyApiUrl.value}/metodo/customer/${customer.CODCLIMKT}`, customer)
      customers.value = customers.value.map((item) =>
        item.CODCLIMKT === data.CODCLIMKT ? data : item
      )
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Update a customer in the local list (used by WebSocket handler).
   */
  function updateCustomerLocally(customer: MetodoCustomer): void {
    customers.value = customers.value.map((item) =>
      item.CODCLIMKT === customer.CODCLIMKT ? customer : item
    )
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    loading,
    customersMix,
    customers,
    customersDDM,
    customersHighlighted,
    customersPagination,
    currentOptions,

    // Getters
    customersSelectSRV,
    customersDDMSelectSRV,

    // Actions
    getCustomer,
    fetchCustomersMix,
    fetchHighlightedCount,
    fetchCustomers,
    fetchCustomersDDM,
    patchCustomer,

    // WebSocket helpers
    updateCustomerLocally,
  }
})
